import axios from 'axios';
import { TOKEN, buildPublicApiUrl } from '@/utils/config';

const STORAGE_KEY = 'baby_album_token';
const DEFAULT_CHANNEL = 1000;
const DEFAULT_PAGE_SIZE = 100;
const IMAGE_UPLOAD_MAX_EDGE = 1920;
const IMAGE_UPLOAD_QUALITY = 0.82;
const IMAGE_UPLOAD_COMPRESS_THRESHOLD = 420 * 1024;
const VIDEO_UPLOAD_EXTENSION_BY_MIME = {
    'video/mp4': 'mp4',
    'video/quicktime': 'mov',
    'video/x-quicktime': 'mov',
    'video/x-m4v': 'm4v',
    'video/3gpp': '3gp',
    'video/webm': 'webm'
};
const VIDEO_PLACEHOLDER = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="720" height="720" viewBox="0 0 720 720">
        <defs>
            <linearGradient id="videoBg" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#1A2B49" />
                <stop offset="100%" stop-color="#425E8B" />
            </linearGradient>
        </defs>
        <rect width="720" height="720" rx="56" fill="url(#videoBg)" />
        <circle cx="360" cy="360" r="118" fill="rgba(255,255,255,0.18)" />
        <path d="M325 298 L454 360 L325 422 Z" fill="#FFFFFF" />
        <text x="360" y="552" text-anchor="middle" font-size="40" font-family="Arial, sans-serif" fill="rgba(255,255,255,0.88)">视频</text>
    </svg>
`)}`;

export function createReactionState () {
    return {
        wow: 0,
        smile: 0,
        cute: 0,
        love: 0
    };
}

function buildUploadText (uploadStatus) {
    return uploadStatus === 'done' ? '云端已备份' : '等待上传中';
}

function parseJsonObject (value, fallbackValue) {
    if (Array.isArray(value) || (value && typeof value === 'object')) {
        return value;
    }

    if (typeof value === 'string' && value) {
        try {
            return JSON.parse(value);
        } catch (error) {
            return fallbackValue;
        }
    }

    return fallbackValue;
}

function normalizeReactionState (value) {
    const parsedValue = parseJsonObject(value, {});
    return {
        ...createReactionState(),
        ...(parsedValue || {})
    };
}

function ensureSuccess (responseData, fallbackMessage) {
    if (responseData && responseData.success) {
        return responseData;
    }

    throw new Error(responseData?.errors?.message || fallbackMessage);
}

function resolveChannelFromToken (token) {
    const normalizedToken = String(token || '').replace(/^Bearer\s+/i, '');

    if (!normalizedToken) {
        return '';
    }

    const tokenParts = normalizedToken.split('.');
    if (tokenParts.length < 2) {
        return '';
    }

    try {
        const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padding = '='.repeat((4 - (base64Payload.length % 4)) % 4);
        const payload = JSON.parse(atob(`${base64Payload}${padding}`));
        return String(payload?.data?.channel || '');
    } catch (error) {
        return '';
    }
}

function requestHeaders (token) {
    const normalizedToken = String(token || '').replace(/^Bearer\s+/i, '');
    const resolvedChannel = resolveChannelFromToken(normalizedToken) || String(DEFAULT_CHANNEL);
    const headers = {
        Channel: resolvedChannel
    };

    if (normalizedToken) {
        headers.Authorization = `Bearer ${normalizedToken}`;
    }

    return headers;
}

function buildApiUrl (path) {
    return buildPublicApiUrl(path);
}

function shouldCompressImageFile (file) {
    return file
        && typeof file.type === 'string'
        && file.type.startsWith('image/')
        && !/gif|svg/i.test(file.type)
        && Number(file.size || 0) > IMAGE_UPLOAD_COMPRESS_THRESHOLD
        && typeof document !== 'undefined'
        && typeof URL !== 'undefined';
}

function canvasToBlob (canvas, type, quality) {
    return new Promise(resolve => {
        canvas.toBlob(blob => resolve(blob), type, quality);
    });
}

async function compressImageForUpload (file) {
    if (!shouldCompressImageFile(file)) {
        return file;
    }

    const objectUrl = URL.createObjectURL(file);
    try {
        const image = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = objectUrl;
        });
        const sourceWidth = image.naturalWidth || image.width || 0;
        const sourceHeight = image.naturalHeight || image.height || 0;
        if (!sourceWidth || !sourceHeight) {
            return file;
        }

        const scale = Math.min(1, IMAGE_UPLOAD_MAX_EDGE / Math.max(sourceWidth, sourceHeight));
        if (scale >= 1 && file.size < IMAGE_UPLOAD_COMPRESS_THRESHOLD * 1.6) {
            return file;
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(sourceWidth * scale));
        canvas.height = Math.max(1, Math.round(sourceHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) {
            return file;
        }

        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const blob = await canvasToBlob(canvas, 'image/jpeg', IMAGE_UPLOAD_QUALITY);
        if (!blob || blob.size >= file.size) {
            return file;
        }

        return new File([blob], (file.name || 'album-photo').replace(/\.[^.]+$/, '.jpg'), {
            type: 'image/jpeg',
            lastModified: file.lastModified || Date.now()
        });
    } catch (error) {
        return file;
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

function resolveUploadFileExtension (file, mediaType) {
    const nameExtension = String(file?.name || '').match(/\.([^.]+)$/)?.[1]?.toLowerCase() || '';

    if (mediaType === 'video') {
        const normalizedType = String(file?.type || '').toLowerCase();
        if (VIDEO_UPLOAD_EXTENSION_BY_MIME[normalizedType]) {
            return VIDEO_UPLOAD_EXTENSION_BY_MIME[normalizedType];
        }
        if (nameExtension === 'quicktime' || nameExtension === 'qt') {
            return 'mov';
        }
        return nameExtension || 'mov';
    }

    return nameExtension || 'jpg';
}

function normalizeUploadFileName (file, mediaType) {
    const extension = resolveUploadFileExtension(file, mediaType);
    const fallbackBaseName = mediaType === 'video' ? 'baby-video' : 'album-photo';
    const originalName = String(file?.name || '').trim();
    const baseName = (originalName || fallbackBaseName)
        .replace(/\.[^.]*$/, '')
        .replace(/[^\w.-]+/g, '-')
        .replace(/^-+|-+$/g, '') || fallbackBaseName;

    return `${baseName}.${extension}`;
}

function normalizeVideoForUpload (file) {
    if (!file) {
        return file;
    }

    const uploadName = normalizeUploadFileName(file, 'video');
    const currentName = String(file.name || '');
    const currentType = String(file.type || '');
    const normalizedType = currentType || (uploadName.endsWith('.mov') ? 'video/quicktime' : '');

    if (
        typeof File !== 'function'
        || (currentName === uploadName && (!normalizedType || currentType === normalizedType))
    ) {
        return file;
    }

    try {
        return new File([file], uploadName, {
            type: normalizedType || currentType,
            lastModified: file.lastModified || Date.now()
        });
    } catch (error) {
        return file;
    }
}

export function buildVideoCoverUrl (videoUrl) {
    if (!videoUrl) {
        return '';
    }

    const urlWithoutQuery = videoUrl.split('?')[0];
    const extensionIndex = urlWithoutQuery.lastIndexOf('.');

    if (extensionIndex === -1) {
        return '';
    }

    return `${urlWithoutQuery.slice(0, extensionIndex)}_1.jpg`;
}

function normalizeEntry (entry = {}) {
    const postType = entry.post_type || entry.postType || 'photo';
    const parsedImages = parseJsonObject(entry.images, []);
    const rawImages = Array.isArray(parsedImages) ? parsedImages.filter(Boolean) : [];
    const uploadStatus = entry.upload_status || entry.uploadStatus || 'done';
    const videoUrl = postType === 'video' ? (rawImages[0] || '') : '';
    const thumbnail = entry.thumbnail || (postType === 'video' ? buildVideoCoverUrl(videoUrl) : '');
    const visualImages = postType === 'video'
        ? [thumbnail || VIDEO_PLACEHOLDER]
        : rawImages;

    return {
        id: String(entry.id || ''),
        postType,
        author: entry.author || '妈妈',
        relativeTime: entry.relative_time || entry.relativeTime || '刚刚',
        caption: entry.caption || '',
        location: entry.location || '',
        uploadStatus,
        uploadText: buildUploadText(uploadStatus),
        images: visualImages,
        videoDuration: entry.video_duration || entry.videoDuration || '',
        selectedReaction: entry.selected_reaction || entry.selectedReaction || '',
        reactions: normalizeReactionState(entry.reactions),
        persisted: true,
        takenAt: Number(entry.taken_at || entry.takenAt || 0) || 0,
        thumbnail,
        videoUrl
    };
}

function normalizeGroup (group = {}) {
    return {
        id: String(group.id || ''),
        label: group.label || group.display_date || group.displayDate || '',
        displayDate: group.display_date || group.displayDate || group.label || '',
        ageText: group.age_text || group.ageText || '',
        entries: Array.isArray(group.entries) ? group.entries.map(normalizeEntry) : []
    };
}

function mergeGroups (currentGroups, incomingGroups) {
    const mergedGroups = currentGroups.map(group => ({
        ...group,
        entries: [...group.entries]
    }));

    incomingGroups.forEach(incomingGroup => {
        const matchedIndex = mergedGroups.findIndex(group => group.id === incomingGroup.id);

        if (matchedIndex === -1) {
            mergedGroups.push({
                ...incomingGroup,
                entries: [...incomingGroup.entries]
            });
            return;
        }

        mergedGroups[matchedIndex] = {
            ...mergedGroups[matchedIndex],
            ...incomingGroup,
            entries: [
                ...mergedGroups[matchedIndex].entries,
                ...incomingGroup.entries
            ]
        };
    });

    return mergedGroups;
}

export function resolveBabyAlbumToken (route) {
    if (route.query.type === 'test') {
        window.localStorage.setItem(STORAGE_KEY, TOKEN);
        return TOKEN;
    }

    const routeToken = route.query.token;
    if (routeToken) {
        window.localStorage.setItem(STORAGE_KEY, routeToken);
        return routeToken;
    }

    return window.localStorage.getItem(STORAGE_KEY) || '';
}

export async function fetchBabyAlbumGroups ({ token, pageSize = DEFAULT_PAGE_SIZE, babyId = '' } = {}) {
    let page = 1;
    let totalCount = 0;
    let mergedGroups = [];

    do {
        const response = await axios.get(buildApiUrl('/baby_album/list'), {
            params: {
                page,
                page_size: pageSize,
                post_type: 'all',
                baby_id: babyId || undefined
            },
            headers: requestHeaders(token)
        });
        const responseData = ensureSuccess(response.data, '加载宝宝相册失败');
        const pageGroups = Array.isArray(responseData.result?.groups)
            ? responseData.result.groups.map(normalizeGroup)
            : [];

        mergedGroups = mergeGroups(mergedGroups, pageGroups);

        totalCount = Number(responseData.result_info?.total_count || 0);
        if (!totalCount || page * pageSize >= totalCount) {
            break;
        }

        page += 1;
    } while (page <= Math.ceil(totalCount / pageSize));

    return mergedGroups;
}

export async function fetchBabyAlbumEntryDetail ({ token, id, babyId = '' }) {
    const response = await axios.get(buildApiUrl('/baby_album/detail'), {
        params: { id, baby_id: babyId || undefined },
        headers: requestHeaders(token)
    });

    return normalizeEntry(ensureSuccess(response.data, '读取动态详情失败').result || {});
}

export async function updateBabyAlbumEntry ({ token, id, caption, location, takenAt, images, thumbnail, babyId = '' }) {
    const payload = { id, baby_id: babyId || undefined };

    if (caption !== undefined) {
        payload.caption = caption;
    }
    if (location !== undefined) {
        payload.location = location;
    }
    if (takenAt !== undefined) {
        payload.taken_at = takenAt;
    }
    if (images !== undefined) {
        payload.images = images;
    }
    if (thumbnail !== undefined) {
        payload.thumbnail = thumbnail;
    }

    const response = await axios.post(buildApiUrl('/baby_album/update'), payload, {
        headers: requestHeaders(token)
    });

    return normalizeEntry(ensureSuccess(response.data, '保存动态失败').result || {});
}

export async function deleteBabyAlbumEntry ({ token, id, babyId = '' }) {
    const response = await axios.post(buildApiUrl('/baby_album/delete'), {
        id,
        baby_id: babyId || undefined
    }, {
        headers: requestHeaders(token)
    });

    return ensureSuccess(response.data, '删除动态失败').result;
}

export async function reactBabyAlbumEntry ({ token, id, reactionKey, babyId = '' }) {
    const response = await axios.post(buildApiUrl('/baby_album/react'), {
        id,
        reaction_key: reactionKey,
        baby_id: babyId || undefined
    }, {
        headers: requestHeaders(token)
    });

    const result = ensureSuccess(response.data, '保存互动失败').result || {};

    return {
        selectedReaction: result.selected_reaction || result.selectedReaction || '',
        reactions: normalizeReactionState(result.reactions)
    };
}

export async function uploadBabyAlbumFile ({ file, mediaType, onProgress }) {
    const uploadFile = mediaType === 'photo' ? await compressImageForUpload(file) : normalizeVideoForUpload(file);
    const formData = new FormData();
    formData.append('file', uploadFile, normalizeUploadFileName(uploadFile, mediaType));

    if (mediaType === 'video') {
        formData.append('type', 1);
    }

    const response = await axios.post(buildApiUrl('/upload/upload'), formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: progressEvent => {
            if (typeof onProgress !== 'function') {
                return;
            }

            const total = Number(progressEvent.total || 0);
            const loaded = Number(progressEvent.loaded || 0);
            if (!total) {
                return;
            }

            const progress = Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
            onProgress(progress);
        }
    });
    const responseData = ensureSuccess(response.data, '媒体上传失败');
    const uploadedUrl = responseData.result?.[0];

    if (!uploadedUrl) {
        throw new Error('媒体上传失败');
    }

    return uploadedUrl;
}

export async function createBabyAlbumEntry ({
    token,
    postType,
    images,
    caption,
    location,
    videoDuration,
    takenAt,
    thumbnail,
    babyId = ''
}) {
    const response = await axios.post(buildApiUrl('/baby_album/create'), {
        images,
        post_type: postType,
        caption,
        location,
        video_duration: videoDuration,
        taken_at: takenAt,
        thumbnail,
        baby_id: babyId || undefined
    }, {
        headers: requestHeaders(token)
    });

    const createdResult = ensureSuccess(response.data, '创建动态失败').result || {};
    const createdId = createdResult.id;

    if (!createdId) {
        throw new Error('创建动态失败');
    }

    try {
        return await fetchBabyAlbumEntryDetail({
            token,
            id: createdId,
            babyId
        });
    } catch (error) {
        return normalizeEntry({
            ...createdResult,
            id: createdId,
            post_type: postType,
            images,
            caption,
            location,
            video_duration: videoDuration,
            taken_at: takenAt,
            thumbnail,
            upload_status: 'done',
            relative_time: '刚刚'
        });
    }
}
